/**
 * Budget Allocation Module - Main Page (Refactored)
 *
 * Dashboard for Meta advertising budget allocation and performance monitoring
 * Uses shared MetaConnectionContext
 */

import { useState, useEffect, useCallback } from 'react'
import * as Icons from 'lucide-react'
import { SummaryCards, CampaignTable } from '../components'
import { budgetAllocationApi } from '../services/api'
import type { AccountSummary, CampaignDetail } from '../types'
import { MetaConnectionGuard } from '@/shared/components/MetaConnectionGuard'
import { useMetaConnection } from '@/shared/contexts'

// ============================================================================
// Date Range Picker Component
// ============================================================================

function DateRangePicker({
  startDate,
  endDate,
  onChange,
  onSearch,
  isLoading,
}: {
  startDate: string
  endDate: string
  onChange: (start: string, end: string) => void
  onSearch: (start: string, end: string) => void
  isLoading?: boolean
}) {
  const presets = [
    { label: '7D', days: 7 },
    { label: '14D', days: 14 },
    { label: '30D', days: 30 },
    { label: '90D', days: 90 },
  ]

  const handlePreset = (days: number) => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - days)
    const startStr = start.toISOString().split('T')[0]
    const endStr = end.toISOString().split('T')[0]
    onChange(startStr, endStr)
    // Pass the dates directly to avoid stale closure issues
    onSearch(startStr, endStr)
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex bg-tf-bg-muted rounded-lg p-1">
        {presets.map((preset) => (
          <button
            key={preset.days}
            onClick={() => handlePreset(preset.days)}
            className="px-3 py-1.5 text-xs font-medium text-tf-text-muted rounded-md hover:bg-tf-bg-card hover:text-tf-text hover:shadow-sm transition-all"
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <input
          type="date"
          value={startDate}
          onChange={(e) => onChange(e.target.value, endDate)}
          className="px-2.5 py-1.5 border border-tf-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
        />
        <span className="text-tf-text-muted">to</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => onChange(startDate, e.target.value)}
          className="px-2.5 py-1.5 border border-tf-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
        />
      </div>
      <button
        onClick={() => onSearch(startDate, endDate)}
        disabled={isLoading}
        className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
      >
        {isLoading ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.Search className="w-4 h-4" />}
        Search
      </button>
    </div>
  )
}

// ============================================================================
// Main Budget Allocation Content (Connected State)
// ============================================================================

function BudgetAllocationContent() {
  const { connection, sync } = useMetaConnection()
  const [summary, setSummary] = useState<AccountSummary | null>(null)
  const [campaigns, setCampaigns] = useState<CampaignDetail[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Date range state
  const today = new Date()
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(today.getDate() - 30)
  const [startDate, setStartDate] = useState(thirtyDaysAgo.toISOString().split('T')[0])
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0])

  // Fetch budget allocation data - accepts optional date parameters to avoid stale closure issues
  const fetchBudgetData = useCallback(
    async (showLoading = false, start?: string, end?: string) => {
      const queryStartDate = start ?? startDate
      const queryEndDate = end ?? endDate

      if (showLoading) {
        setIsSearching(true)
      }

      try {
        const [summaryRes, campaignsRes] = await Promise.all([
          budgetAllocationApi.getSummary(queryStartDate, queryEndDate),
          budgetAllocationApi.getCampaigns(queryStartDate, queryEndDate),
        ])

        if (summaryRes.success && summaryRes.data) {
          setSummary(summaryRes.data)
        }
        if (campaignsRes.success && campaignsRes.data) {
          setCampaigns(campaignsRes.data)
        }
      } catch (err) {
        console.error('Error fetching budget data:', err)
        setError('Failed to load budget allocation data')
      } finally {
        setIsSearching(false)
      }
    },
    [startDate, endDate]
  )

  // Handle search button click - pass current dates to avoid stale closure
  const handleSearch = useCallback((start?: string, end?: string) => {
    fetchBudgetData(true, start ?? startDate, end ?? endDate)
  }, [fetchBudgetData, startDate, endDate])

  // Trigger sync
  const handleSync = async () => {
    setIsSyncing(true)
    setError(null)

    try {
      await sync()
      // Wait a bit then refresh
      await new Promise((resolve) => setTimeout(resolve, 2000))
      try {
        await fetchBudgetData()
      } catch (fetchErr) {
        console.error('Error fetching data after sync:', fetchErr)
        setError('Sync completed but failed to refresh data')
      }
    } catch (err) {
      console.error('Error syncing data:', err)
      setError('Failed to sync data')
    } finally {
      setIsSyncing(false)
    }
  }

  // Initial data load
  useEffect(() => {
    if (connection.connected) {
      fetchBudgetData(false)
    }
  }, [connection.connected, fetchBudgetData])

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-tf-text">Budget Allocation</h1>
          <p className="text-sm text-tf-text-muted mt-1">Monitor and optimize your Meta advertising performance</p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start)
              setEndDate(end)
            }}
            onSearch={handleSearch}
            isLoading={isSearching}
          />
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary bg-tf-bg-card border border-primary rounded-lg hover:bg-tf-feedback-info-bg transition-colors disabled:opacity-50"
          >
            {isSyncing ? (
              <>
                <Icons.Loader2 className="w-4 h-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <Icons.RefreshCw className="w-4 h-4" />
                Sync Data
              </>
            )}
          </button>
        </div>
      </div>

      {/* Connection Info Banner */}
      {connection.accountId && (
        <div className="flex items-center justify-between p-4 bg-tf-feedback-info-bg border border-primary/20 rounded-lg">
          <div className="flex items-center gap-3">
            <Icons.CheckCircle2 className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm font-medium text-tf-text">
                Connected to {connection.accountName || `Ad Account ${connection.accountId}`}
              </p>
              {connection.lastSyncAt && (
                <p className="text-xs text-tf-text-muted mt-0.5">
                  Last synced: {new Date(connection.lastSyncAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-tf-feedback-error-bg border border-tf-feedback-error-border rounded-lg">
          <Icons.AlertCircle className="w-5 h-5 text-tf-feedback-error-text flex-shrink-0" />
          <p className="text-sm text-tf-feedback-error-text">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-tf-feedback-error-text hover:opacity-80">
            <Icons.X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <SummaryCards summary={summary} loading={isSearching} />

      {/* Campaign Table */}
      <CampaignTable campaigns={campaigns} loading={isSearching} />
    </div>
  )
}

// ============================================================================
// Main Budget Allocation Page (with Connection Guard)
// ============================================================================

export default function BudgetAllocationPage() {
  return (
    <MetaConnectionGuard
      title="Connect Meta Ads Account"
      description="Connect your Meta Ads account to view budget allocation and campaign performance data."
      icon="PieChart"
    >
      <BudgetAllocationContent />
    </MetaConnectionGuard>
  )
}

